"use client";

import { ErrorMessage } from "@hookform/error-message";
import { Label } from "radix-ui";
import { type ComponentProps, forwardRef, useId } from "react";
import { type FieldValues, type RegisterOptions, useFormContext } from "react-hook-form";
import { FormErrorMessage, cn } from "../../..";

interface DateSelectorProps extends ComponentProps<"input"> {
	label: string;
	registerOptions?: RegisterOptions<FieldValues>;
}

export const DateSelector = forwardRef<HTMLInputElement, DateSelectorProps>(
	function DateSelectorRef(props, ref) {
		const { label, className, registerOptions, ...remainingProps } = props;
		const id = useId();
		const methods = useFormContext();
		const registeredProps =
			methods && props.name
				? methods.register(props.name, { shouldUnregister: true, ...registerOptions })
				: { ref };
		const invalid = props.name ? methods?.getFieldState(props.name)?.invalid : false;

		return (
			<div className={cn(className, "flex flex-col gap-1")}>
				<Label.Root htmlFor={id} className="self-start font-bold">
					{label}
					{props.required && "*"}
				</Label.Root>

				<input
					id={id}
					type="date"
					{...registeredProps}
					{...remainingProps}
					className={cn(
						"form-input rounded border-shades read-only:bg-shades-light/70 hover:border-primary-medium focus:outline-none focus-visible:border-primary-medium focus-visible:ring-primary-medium focus-visible:ring-inset disabled:cursor-not-allowed disabled:bg-shades-light/70 motion-safe:transition-colors",
						{
							"border-danger ring-danger focus-visible:border-danger focus-visible:ring-danger":
								invalid,
						},
					)}
				/>

				{invalid && !!props.name && (
					<FormErrorMessage variant="danger">
						<ErrorMessage name={props.name} />
					</FormErrorMessage>
				)}
			</div>
		);
	},
);
